import type { EquipmentPortPose, EquipmentPortVisual, Vec3 } from '../equipmentPortTransfer';
import type { EquipmentFoupSlotOccupant } from '../equipmentFoupSlots';
import type { FabToolLayout } from './fabToolLayout';

function dist3(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t));
}

/** Load-port indices where a FOUP is resting on the deck (not in chamber). */
export function resolveFabLoadPortOccupancy(
  portVisuals: EquipmentPortVisual[] | undefined,
  poses: EquipmentPortPose[]
): number[] {
  if (!portVisuals?.length) return [];
  const out: number[] = [];
  for (const visual of portVisuals) {
    if (visual.foupMode !== 'resting' || !visual.foupPosition) continue;
    const pose = poses[visual.portIndex];
    if (!pose) continue;
    const nearDeck =
      dist3(visual.foupPosition, pose.loadPort) < dist3(visual.foupPosition, pose.internal);
    if (nearDeck) out.push(visual.portIndex);
  }
  return out;
}

export type ChamberFoupVisual = {
  foupId?: string;
  position: Vec3;
  slotIndex: number;
};

/** All chamber-resting FOUPs from canonical slot state + transfer visuals. */
export function resolveFabChamberFoups(
  portVisuals: EquipmentPortVisual[] | undefined,
  poses: EquipmentPortPose[],
  chamberSlots: readonly EquipmentFoupSlotOccupant[],
  animatingFoupId?: string | null
): ChamberFoupVisual[] {
  const bySlot = new Map<number, ChamberFoupVisual>();

  for (const slot of chamberSlots) {
    if (slot.foupId === animatingFoupId) continue;
    bySlot.set(slot.slotIndex, {
      foupId: slot.foupId,
      position: slot.position,
      slotIndex: slot.slotIndex,
    });
  }

  if (portVisuals?.length) {
    for (const visual of portVisuals) {
      if (visual.foupMode !== 'resting' || !visual.foupPosition) continue;
      if (visual.foupId && visual.foupId === animatingFoupId) continue;
      const pose = poses[visual.portIndex];
      if (!pose) continue;
      if (dist3(visual.foupPosition, pose.internal) >= dist3(visual.foupPosition, pose.loadPort) * 0.85) {
        continue;
      }
      const existing = [...bySlot.values()].find((v) => v.foupId === visual.foupId);
      if (existing) {
        existing.position = visual.foupPosition;
        continue;
      }
      if (visual.foupId && [...bySlot.values()].some((v) => v.foupId === visual.foupId)) continue;
      const slotIndex = visual.portIndex;
      if (!bySlot.has(slotIndex)) {
        bySlot.set(slotIndex, {
          foupId: visual.foupId,
          position: visual.foupPosition,
          slotIndex,
        });
      }
    }
  }

  return [...bySlot.values()].sort((a, b) => a.slotIndex - b.slotIndex);
}

/** @deprecated Use resolveFabChamberFoups — first chamber FOUP only. */
export function resolveFabChamberFoupPosition(
  portVisuals: EquipmentPortVisual[] | undefined,
  poses: EquipmentPortPose[]
): Vec3 | null {
  const foups = resolveFabChamberFoups(portVisuals, poses, []);
  return foups[0]?.position ?? null;
}

export type FabLifterMotion = {
  liftProgress: number;
  forkLocalX: number;
  forkLocalZ: number;
};

export function resolveFabLifterMotion(
  flyingPosition: Vec3 | null | undefined,
  poses: EquipmentPortPose[],
  activePortIndex: number | null,
  layout: FabToolLayout
): FabLifterMotion {
  const idle = { liftProgress: 0, forkLocalX: 0, forkLocalZ: 0 };
  if (!flyingPosition || activePortIndex == null) return idle;
  const pose = poses[activePortIndex];
  if (!pose) return idle;

  const span = pose.internal[1] - pose.loadPort[1];
  const liftProgress =
    Math.abs(span) < 0.001 ? 0 : clamp01((flyingPosition[1] - pose.loadPort[1]) / span);

  return {
    liftProgress,
    forkLocalX: flyingPosition[0] - layout.glassX,
    forkLocalZ: flyingPosition[2] - layout.glassZ + layout.chamberDepth * 0.08,
  };
}

export function resolveFabLifterProgress(
  flyingPosition: Vec3 | null | undefined,
  poses: EquipmentPortPose[],
  activePortIndex: number | null
): number {
  if (!flyingPosition || activePortIndex == null) return 0;
  const pose = poses[activePortIndex];
  if (!pose) return 0;
  const span = pose.internal[1] - pose.loadPort[1];
  if (Math.abs(span) < 0.001) return 0;
  return clamp01((flyingPosition[1] - pose.loadPort[1]) / span);
}
